import { makeAutoObservable } from 'mobx';
import { LoginViewModel } from '@pages/auth/Login/types';
import { NavigateFunction } from 'react-router-dom';
import { PATH_AUTH } from '@app/routes/paths';
import { queryClient } from '@shared/api/reactQuery';
import { inject, injectable } from 'inversify';
import { reformatPhone } from '@shared/utils/helpers/formatString';
import { notifications } from '@shared/utils/notifications';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
// OSI-797: коды ошибок авторизации и их i18n-ключи + расширенный тип ответа с lockDurationMinutes
import { AUTH_ERROR_CODE, AUTH_ERROR_MESSAGE } from '@pages/auth/Login/config/errorCodes';
import type { AuthResponse } from '@pages/auth/Login/model/authResponse';
import { getGetApiAuthGenerateOtpPhoneQueryOptions, getPostApiAuthMutationOptions } from '@/shared/api/orval/auth/auth';

type States = 'pin' | 'shareContact' | 'otp';

@injectable()
export class ViewModel implements LoginViewModel {
  phone = '';

  navigate: NavigateFunction | null;

  isContacted = false;

  botUrl = '';

  state: States = 'pin';

  pin = '';

  /** OSI-797: i18n-ключ ошибки для алерта над формой пароля (null — алерта нет) */
  authErrorKey: string | null = null;

  /** OSI-797: модалка блокировки пользователя */
  isBlocked = false;

  /** OSI-797: длительность блокировки в минутах (подстановка в текст модалки) */
  blockedMinutes = 0;

  /**
   * OSI-797: счётчик неудачных попыток, используется как React key для PinCode/OtpCheck.
   * Инкремент гарантированно размонтирует форму и обнулит состояние react-hook-form
   * (4-значный код хранится только внутри OtpCheck и в VM его нет).
   */
  attemptId = 0;

  constructor(@inject(AuthServiceIdentifier) private authModule: AuthService) {
    makeAutoObservable(this);
  }

  setPin = async (rawPin: any) => {
    // OSI-797: пользователь начал новую попытку — старый алерт с ошибкой убираем
    this.clearAuthError();

    const pin = Object.entries(rawPin)
      // eslint-disable-next-line no-nested-ternary
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      .map((e) => e[1])
      .join('');

    this.pin = pin;

    if (!this.isContacted) {
      this.changeState('shareContact');
    } else {
      try {
        await this.generateOtp();
        this.changeState('otp');
      } catch (e) {
        console.error(e);
      }
    }
  };

  auth = async (rawOtp: any) => {
    if (!this.navigate || !rawOtp || !this.phone) return;

    const otp = Object.entries(rawOtp)
      // eslint-disable-next-line no-nested-ternary
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      .map((e) => e[1])
      .join('');
    try {
      this.clearAuthError();

      const reformattedPhone = reformatPhone(this.phone);

      const options = getPostApiAuthMutationOptions();
      const mutation = queryClient.getMutationCache().build(queryClient, options);

      const response = (await mutation.execute({
        params: { username: reformattedPhone, password: this.pin, otp }
      })) as AuthResponse;

      // OSI-797: штатные ошибки приходят HTTP 200 + code !== 0, разбираем по коду
      if (response?.code !== 0) {
        this.handleAuthError(response);
        return;
      }

      if (response.result?.hadActiveToken) {
        notifications.warning('auth:login.hadActiveToken');
      }

      if (response.result?.userId) {
        queryClient.setQueryData(['userId'], response.result.userId);
        await this.authModule.setAuthData({ userId: response.result.userId });
        this.navigate(PATH_AUTH.selectRole);
        return;
      }

      // OSI-797: code === 0, но userId не пришёл — некорректный ответ, трактуем как общую ошибку
      this.failAttempt('auth:login.wrongCredentials');
    } catch (e) {
      // OSI-797: сюда попадают только нештатные ситуации (сеть, таймаут, 5xx).
      // Раньше здесь был navigate на /auth/check-contact — он стирал location.state.phone,
      // из-за чего пользователь терял введённый номер. Теперь остаёмся на /auth/login.
      console.error(e);
      notifications.error();
      this.failAttempt(null);
    }
  };

  onShareContactSuccess = async () => {
    try {
      await this.generateOtp();
      this.changeState('otp');
    } catch (e) {
      console.error(e);
    }
  };

  /** OSI-797: разбор кода ошибки авторизации */
  private handleAuthError = (response: AuthResponse | undefined) => {
    const code = response?.code;

    // Блокировка — показываем модалку, алерт над формой не нужен
    if (code === AUTH_ERROR_CODE.tooManyAttempts) {
      this.blockedMinutes = response?.result?.lockDurationMinutes ?? 0;
      this.isBlocked = true;
      this.failAttempt(null);
      return;
    }

    const key = code != null ? AUTH_ERROR_MESSAGE[code] : undefined;

    // Неизвестный код: не молчим — показываем сообщение бэка тостом,
    // но всё равно возвращаем на шаг ввода пароля с очисткой полей
    if (!key) {
      if (response?.message) notifications.error(response.message);
      this.failAttempt('auth:login.wrongCredentials');
      return;
    }

    this.failAttempt(key);
  };

  /**
   * OSI-797: общий сброс после неудачной попытки — очищаем накопленный пароль,
   * инкрементим attemptId (форсированный remount форм пароля и кода),
   * возвращаем на шаг 'pin'. Номер телефона (this.phone) НЕ трогаем.
   */
  private failAttempt = (errorKey: string | null) => {
    this.pin = '';
    this.attemptId += 1;
    this.changeState('pin');
    this.authErrorKey = errorKey;
  };

  clearAuthError = () => {
    this.authErrorKey = null;
  };

  /** OSI-797: возврат на шаг пароля из UI вместо прямой записи state снаружи */
  goToPin = () => {
    this.changeState('pin');
  };

  dismissBlock = () => {
    this.isBlocked = false;
    this.blockedMinutes = 0;
  };

  private generateOtp = async () => {
    const reformattedPhone = reformatPhone(this.phone);
    const options = getGetApiAuthGenerateOtpPhoneQueryOptions(reformattedPhone, {
      query: {
        gcTime: 0,
        staleTime: 0
      }
    });
    const response = await queryClient.fetchQuery(options);
    return response;
  };

  private changeState = (state: States) => {
    this.state = state;
  };
}
